/**
 * App Store Settings Page
 * Configure app store URLs, generate QR codes, and view download statistics
 */

import React, { useState, useEffect } from 'react';
import { useAdmin } from '../AdminApp';
import { toast } from 'sonner';
import {
  Smartphone,
  Save,
  RefreshCw,
  ExternalLink,
  QrCode,
  Download,
  Settings,
  Apple,
  Play,
  Copy,
  Check,
  Info
} from 'lucide-react';

// QR Code Generator Component (using QR Server API)
const QRCodeDisplay = ({ url, title, color }) => {
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(url)}&color=${color.replace('#', '')}`;
  
  const downloadQR = () => {
    const link = document.createElement('a');
    link.href = qrUrl;
    link.download = `${title.replace(/\s+/g, '_')}_QR.png`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    toast.success('QR Code downloaded!');
  };
  
  return (
    <div className="text-center">
      <div className="bg-white p-4 rounded-xl inline-block shadow-sm border border-gray-100">
        <img 
          src={qrUrl} 
          alt={`${title} QR Code`}
          className="w-40 h-40"
        />
      </div>
      <p className="text-sm text-gray-500 mt-2">{title}</p>
      <button
        onClick={downloadQR}
        className="mt-2 text-xs text-emerald-600 hover:text-emerald-700 flex items-center gap-1 mx-auto"
      >
        <Download className="w-3 h-3" /> Download QR
      </button>
    </div>
  );
};

const AppStoreSettingsPage = () => {
  const { api } = useAdmin();
  const [configs, setConfigs] = useState({
    google_play: {
      store_url: '',
      is_published: false,
      app_version: '1.0.0-beta',
      release_notes: ''
    },
    apple_store: {
      store_url: '',
      is_published: false,
      app_version: '1.0.0-beta',
      release_notes: ''
    }
  });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(null);
  const [policy, setPolicy] = useState(null);       // Round 66: force updates
  const [installs, setInstalls] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [configRes, statsRes, policyRes] = await Promise.all([
        api.get('/api/admin/app-store/config'),
        api.get('/api/admin/app-store/stats?days=30'),
        api.get('/api/admin/app-store/update-policy').catch(() => ({ data: null })),
      ]);
      if (policyRes.data) { setPolicy(policyRes.data.policy); setInstalls(policyRes.data.installs); }
      
      // Transform config array to object
      const configObj = { google_play: {}, apple_store: {} };
      (configRes.data.configs || []).forEach(c => {
        configObj[c.store_type] = {
          store_url: c.store_url || '',
          is_published: c.is_published || false,
          app_version: c.app_version || '1.0.0-beta',
          release_notes: c.release_notes || ''
        };
      });
      
      setConfigs(configObj);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to load app store settings');
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async (storeType) => {
    try {
      setSaving(true);
      const config = configs[storeType];
      
      await api.post('/api/admin/app-store/config', {
        store_type: storeType,
        store_url: config.store_url,
        is_published: config.is_published,
        app_version: config.app_version,
        release_notes: config.release_notes
      });
      
      toast.success(`${storeType === 'google_play' ? 'Google Play' : 'Apple Store'} settings saved!`);
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Round 66: the update policy
  const savePolicy = async () => {
    if (!policy) return;
    try {
      setSaving(true);
      const r = await api.post('/api/admin/app-store/update-policy', {
        require_latest: !!policy.require_latest, grace_hours: Number(policy.grace_hours), message: policy.message || '',
        ios: { min_version: policy.ios?.min_version || '', latest_version: policy.ios?.latest_version || '' },
        android: { min_version: policy.android?.min_version || '', latest_version: policy.android?.latest_version || '' },
      });
      setPolicy(r.data.policy);
      toast.success('Update policy saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save the update policy');
    } finally {
      setSaving(false);
    }
  };
  const refreshPolicy = async () => {
    try {
      const r = await api.post('/api/admin/app-store/update-policy/refresh');
      setPolicy(r.data.policy);
      toast.success(r.data.ios_latest ? `The App Store offers ${r.data.ios_latest}` : 'The App Store has no listing to read yet');
    } catch (e) {
      toast.error('Could not ask the App Store');
    }
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
    toast.success('Copied to clipboard!');
  };

  const updateConfig = (storeType, field, value) => {
    setConfigs(prev => ({
      ...prev,
      [storeType]: {
        ...prev[storeType],
        [field]: value
      }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-emerald-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">App Store Settings</h1>
          <p className="text-gray-500 text-sm mt-1">Configure download links, QR codes, and track installs</p>
        </div>
        <button
          onClick={fetchData}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-sm font-medium text-gray-700"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Beta Notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-amber-800">Beta Version</p>
          <p className="text-sm text-amber-700 mt-1">
            Configure your placeholder app store URLs below. Once your app is published to Google Play and Apple App Store, 
            update the URLs here and toggle "Published" to enable the download buttons on the landing page.
          </p>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-emerald-50 rounded-lg">
              <Play className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats?.google_play?.total_clicks || 0}</p>
              <p className="text-sm text-gray-500">Google Play Clicks</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-50 rounded-lg">
              <Apple className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats?.apple_store?.total_clicks || 0}</p>
              <p className="text-sm text-gray-500">Apple Store Clicks</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-amber-50 rounded-lg">
              <Download className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats?.total_clicks_all_time || 0}</p>
              <p className="text-sm text-gray-500">Total Clicks</p>
            </div>
          </div>
        </div>
      </div>

      {/* Round 66: force updates */}
      {policy && (
        <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="app-update-policy">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h3 className="font-semibold text-gray-900">Force updates</h3>
              <p className="text-sm text-gray-500 mt-0.5">The apps ask the server on every launch. A version below the minimum sees an "Update GEMS" page it cannot close. With "require the latest" on, a new store version becomes the minimum after the grace hours; before that the apps only recommend it.</p>
            </div>
            <button onClick={refreshPolicy} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm text-gray-700"><RefreshCw className="w-4 h-4" /> Ask the App Store now</button>
          </div>
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <label className="text-sm text-gray-700 flex items-center gap-2 lg:col-span-2">
              <input type="checkbox" checked={!!policy.require_latest} onChange={(e) => setPolicy({ ...policy, require_latest: e.target.checked })} /> Require the latest store version (after the grace)
            </label>
            <label className="text-sm text-gray-700">Grace hours
              <input type="number" min="0" max="168" value={policy.grace_hours ?? 24} onChange={(e) => setPolicy({ ...policy, grace_hours: e.target.value })} className="mt-1 block w-full border rounded-lg px-2 py-1.5 text-sm" />
            </label>
            <label className="text-sm text-gray-700 lg:col-span-4">Message on the update page
              <input value={policy.message || ''} maxLength={200} onChange={(e) => setPolicy({ ...policy, message: e.target.value })} className="mt-1 block w-full border rounded-lg px-2 py-1.5 text-sm" />
            </label>
            {['ios', 'android'].map((p) => (
              <div key={p} className="lg:col-span-2 border rounded-lg p-3">
                <p className="text-sm font-semibold text-gray-900">{p === 'ios' ? 'iOS' : 'Android'}</p>
                <p className="text-xs text-gray-500">Latest in the store: <b>{policy[p]?.latest_version || '—'}</b>{policy[p]?.latest_seen_at ? ` · first seen ${new Date(policy[p].latest_seen_at).toLocaleString()} · ${policy[p].latest_source === 'app_store' ? 'read from the App Store' : 'typed here'}` : ''}</p>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <label className="text-xs text-gray-700">Minimum version
                    <input value={policy[p]?.min_version || ''} placeholder="1.5.2" onChange={(e) => setPolicy({ ...policy, [p]: { ...(policy[p] || {}), min_version: e.target.value } })} className="mt-1 block w-full border rounded-lg px-2 py-1.5 text-sm" />
                  </label>
                  <label className="text-xs text-gray-700">{p === 'ios' ? 'Latest (override)' : 'Latest on Google Play'}
                    <input value={policy[p]?.latest_version || ''} placeholder="1.5.2" onChange={(e) => setPolicy({ ...policy, [p]: { ...(policy[p] || {}), latest_version: e.target.value } })} className="mt-1 block w-full border rounded-lg px-2 py-1.5 text-sm" />
                  </label>
                </div>
                {(installs?.[p] || []).length > 0 && (
                  <p className="text-xs text-gray-500 mt-2">Devices this week: {installs[p].map((r) => `${r.version} ×${r.devices}`).join(' · ')}</p>
                )}
              </div>
            ))}
          </div>
          <div className="mt-3 flex items-center gap-3">
            <button onClick={savePolicy} disabled={saving} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold disabled:opacity-50"><Save className="w-4 h-4" /> Save update policy</button>
            <span className="text-xs text-gray-500">iOS reads the App Store by itself. For Android, type the version here when you promote a build on Google Play.</span>
          </div>
        </div>
      )}

      {/* Store Configurations */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Google Play Store */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-emerald-50 to-green-50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white rounded-lg shadow-sm">
                <Play className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Google Play Store</h3>
                <p className="text-sm text-gray-500">Android app configuration</p>
              </div>
            </div>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Store URL</label>
              <div className="flex gap-2">
                <input
                  type="url"
                  value={configs.google_play.store_url}
                  onChange={(e) => updateConfig('google_play', 'store_url', e.target.value)}
                  placeholder="https://play.google.com/store/apps/details?id=..."
                  className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                />
                <button
                  onClick={() => copyToClipboard(configs.google_play.store_url, 'google')}
                  className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50"
                  title="Copy URL"
                >
                  {copied === 'google' ? <Check className="w-5 h-5 text-emerald-500" /> : <Copy className="w-5 h-5 text-gray-400" />}
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">App Version</label>
                <input
                  type="text"
                  value={configs.google_play.app_version}
                  onChange={(e) => updateConfig('google_play', 'app_version', e.target.value)}
                  placeholder="1.0.0-beta"
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Status</label>
                <label className="flex items-center gap-2 mt-2">
                  <input
                    type="checkbox"
                    checked={configs.google_play.is_published}
                    onChange={(e) => updateConfig('google_play', 'is_published', e.target.checked)}
                    className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                  />
                  <span className="text-sm text-gray-600">Published</span>
                </label>
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Release Notes</label>
              <textarea
                value={configs.google_play.release_notes}
                onChange={(e) => updateConfig('google_play', 'release_notes', e.target.value)}
                placeholder="What's new in this version..."
                rows="2"
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
              />
            </div>
            
            <div className="flex items-center justify-between pt-4 border-t border-gray-100">
              <a
                href={configs.google_play.store_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
              >
                <ExternalLink className="w-4 h-4" /> Test Link
              </a>
              <button
                onClick={() => saveConfig('google_play')}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium disabled:opacity-50"
              >
                <Save className="w-4 h-4" /> Save
              </button>
            </div>
          </div>
        </div>

        {/* Apple App Store */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white rounded-lg shadow-sm">
                <Apple className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Apple App Store</h3>
                <p className="text-sm text-gray-500">iOS app configuration</p>
              </div>
            </div>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Store URL</label>
              <div className="flex gap-2">
                <input
                  type="url"
                  value={configs.apple_store.store_url}
                  onChange={(e) => updateConfig('apple_store', 'store_url', e.target.value)}
                  placeholder="https://apps.apple.com/app/..."
                  className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
                <button
                  onClick={() => copyToClipboard(configs.apple_store.store_url, 'apple')}
                  className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50"
                  title="Copy URL"
                >
                  {copied === 'apple' ? <Check className="w-5 h-5 text-blue-500" /> : <Copy className="w-5 h-5 text-gray-400" />}
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">App Version</label>
                <input
                  type="text"
                  value={configs.apple_store.app_version}
                  onChange={(e) => updateConfig('apple_store', 'app_version', e.target.value)}
                  placeholder="1.0.0-beta"
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Status</label>
                <label className="flex items-center gap-2 mt-2">
                  <input
                    type="checkbox"
                    checked={configs.apple_store.is_published}
                    onChange={(e) => updateConfig('apple_store', 'is_published', e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-600">Published</span>
                </label>
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Release Notes</label>
              <textarea
                value={configs.apple_store.release_notes}
                onChange={(e) => updateConfig('apple_store', 'release_notes', e.target.value)}
                placeholder="What's new in this version..."
                rows="2"
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
            
            <div className="flex items-center justify-between pt-4 border-t border-gray-100">
              <a
                href={configs.apple_store.store_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
              >
                <ExternalLink className="w-4 h-4" /> Test Link
              </a>
              <button
                onClick={() => saveConfig('apple_store')}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
              >
                <Save className="w-4 h-4" /> Save
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* QR Codes Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center gap-2 mb-6">
          <QrCode className="w-5 h-5 text-gray-600" />
          <h2 className="text-lg font-semibold text-gray-900">QR Codes</h2>
        </div>
        <p className="text-sm text-gray-500 mb-6">
          Share these QR codes for easy mobile app downloads. Users can scan to go directly to the app store.
        </p>
        <div className="flex flex-wrap justify-center gap-8">
          {configs.google_play.store_url && (
            <QRCodeDisplay 
              url={configs.google_play.store_url} 
              title="Google Play Store"
              color="10B981"
            />
          )}
          {configs.apple_store.store_url && (
            <QRCodeDisplay 
              url={configs.apple_store.store_url} 
              title="Apple App Store"
              color="3B82F6"
            />
          )}
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
        <h3 className="font-semibold text-gray-900 mb-3">How to Get Your App Store URLs</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-gray-600">
          <div>
            <p className="font-medium text-emerald-700 mb-2">Google Play Store:</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Go to <a href="https://play.google.com/console" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline">Google Play Console</a></li>
              <li>Select your app</li>
              <li>Copy the URL from your browser</li>
              <li>Format: play.google.com/store/apps/details?id=YOUR_PACKAGE_NAME</li>
            </ol>
          </div>
          <div>
            <p className="font-medium text-blue-700 mb-2">Apple App Store:</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Go to <a href="https://appstoreconnect.apple.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">App Store Connect</a></li>
              <li>Select your app</li>
              <li>Go to App Information</li>
              <li>Copy the App Store URL</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppStoreSettingsPage;
